import { crossBrowserExitFullscreen } from 'util/fullscreen';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LocalVideoTrack, Participant, RemoteVideoTrack } from 'livekit-client';
import isNil from 'lodash/isNil';
import { ScreenShare } from 'features/ScreenShare';
import { ControlsPanel, FloatActiveSpeaker } from 'features/Room/components';
import { selectLocalParticipant, selectRecentSpeaker, selectRecentSpeakerId } from 'state/selectors';
import { useDoubleClick } from 'ui/hooks/useDoubleClick';
import { useShowOnMovingPointer } from 'ui/hooks/useShowOnMovingPointer';
import { isElectron } from '@web-chat-core/ui-kit/helpers/electron';
import { StageProps } from '../types';
import { Toolbar } from './components/Toolbar';
import {
    ControlsArea,
    HeaderLayout,
    MainLayout,
    MobileExitFullscreenButton,
    MobileFloatActiveSpeakerButton,
    MobileFloatActiveSpeakerWindow,
    SpeakerMobileLayout,
} from './view';

type ScreenShareStageProps = StageProps & {
    participants: Participant[];
    screenTrack: LocalVideoTrack | RemoteVideoTrack;
};

export const MobileFullScreenShareStage = ({
    roomState,
    controlRenderer,
    onLeave,
    participants,
    screenTrack,
}: ScreenShareStageProps) => {
    const { t } = useTranslation();

    const enableScreenShare = isElectron ? true : false;

    const handleExitFullscreen = useCallback(() => {
        crossBrowserExitFullscreen();
    }, []);

    const ref = useDoubleClick<HTMLDivElement>({
        callback: handleExitFullscreen,
    });

    // В mobile fullscreen изначально прячем интерфейс
    const { isInterfaceShown, isInterfaceHidden } = useShowOnMovingPointer({ ref: ref });

    const recentSpeakerId = selectRecentSpeakerId();
    const recentSpeaker = useMemo(() => {
        const recentSpeaker = selectRecentSpeaker();
        if (!recentSpeaker) {
            return selectLocalParticipant();
        }

        return recentSpeaker;
    }, [recentSpeakerId]);

    const { room } = roomState;

    // TODO: Demo modernization. Невозможная ситуация, подумать как лучше убрать.
    if (!room) {
        return <div>{t('web_call_room_closed')}</div>;
    }

    const ControlRenderer = controlRenderer ?? ControlsPanel;

    return (
        <SpeakerMobileLayout $isFullscreenActive $isWide>
            <HeaderLayout $hidden={isInterfaceHidden}>
                <Toolbar participants={participants} />
            </HeaderLayout>

            <MainLayout>
                <ScreenShare shareRef={ref} track={screenTrack} isAuthorShown={isInterfaceShown} />
                {!isNil(recentSpeaker) && (
                    <FloatActiveSpeaker
                        participant={recentSpeaker}
                        openButtonHidden={isInterfaceHidden}
                        ButtonComponent={MobileFloatActiveSpeakerButton}
                        WindowComponent={MobileFloatActiveSpeakerWindow}
                    />
                )}
                <MobileExitFullscreenButton
                    onClick={handleExitFullscreen}
                    isFullscreenActive
                    isHidden={isInterfaceHidden}
                />
            </MainLayout>

            <ControlsArea $hidden={isInterfaceHidden}>
                <ControlRenderer
                    room={room}
                    enableScreenShare={enableScreenShare}
                    onLeave={onLeave}
                    isInterfaceHidden={isInterfaceHidden}
                />
            </ControlsArea>
        </SpeakerMobileLayout>
    );
};
